import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import get from 'hs-lodash/get';
import { useState, useEffect } from 'react';
import useFlowId from 'automation-ui-routing/hooks/useFlowId';
import { useQuery } from 'data-fetching-client';
import { useLocation, useHistory } from 'react-router-dom';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIPanel from 'UIComponents/panel/UIPanel';
import UIPanelHeader from 'UIComponents/panel/UIPanelHeader';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UITable from 'UIComponents/table/UITable';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import UIAbstractProgress from 'UIComponents/progress/UIAbstractProgress';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import AuditEventHistory from 'automation-ui-management/records/AuditHistory/AuditEventHistory';
import * as AuditEventTypes from 'automation-ui-management/records/AuditHistory/AuditEventTypes';
import { GET_FLOW_REVISIONS } from 'automation-ui-management/queries/flowRevisionQueries';
import { GET_FLOW_BY_ID, FLOW_BY_ID_FIELDNAME } from 'automation-ui-management/queries/flowQueries';
import RevisionRollupRow from 'automation-ui-management/revision-rollup-panel/RevisionRollupRow';
import { ALIGNED_TIMESTAMP_COLUMN_WIDTH } from 'automation-ui-management/revision-rollup-panel/RevisionRollupInsetTable';
import AuditHistoryFilters from 'automation-ui-management/audit-history/AuditHistoryFilters';
import AuditEventHistoryFilters from 'automation-ui-management/records/AuditHistory/AuditEventHistoryFilters';
import { getDefaultCustomDateRangeForFlow } from 'automation-ui-management/utils/dateRangeUtils';
import AuditEventHistoryFilterKeys from 'automation-ui-management/records/AuditHistory/AuditEventHistoryFilterKeys';
import GenericErrorMessage from 'automation-ui-management/components/errors/GenericErrorMessage';
import { SIDE_PANEL_WIDTH } from 'automation-ui-core/constants/ui';
import { compose } from 'redux';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import Gates from 'automation-ui-management/constants/Gates';
import { useAuditEventRollups } from 'automation-ui-management/revision-rollup-panel/useAuditEventRollups';
import { NetworkStatus } from '@apollo/client';
import { atomicQueryUpdate } from 'automation-ui-routing/historyUtils';
import { ALL_PRESET_ID } from 'automation-ui-management/constants/DateRangePickerOptions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AuditEventTypesLookup = AuditEventTypes;
const RevisionRollupPanel = ({
  onClose,
  onRevisionSelect,
  renderNavMarker,
  renderFailureNavMarker,
  isUngated
}) => {
  const flowId = useFlowId();
  const location = useLocation();
  const history = useHistory();
  const trackEvent = useTrackEvent();
  const [auditEvents, setAuditEvents] = useState(new AuditEventHistory({}));
  const [filters, setFilters] = useState(new AuditEventHistoryFilters().updateFilterFromLocation(location));
  const [nextPage, setNextPage] = useState(undefined);
  const [expandedRowKey, setExandedRowKey] = useState(0);
  useEffect(() => {
    setFilters(new AuditEventHistoryFilters().updateFilterFromLocation(location));
  }, [location]);
  const {
    userId,
    cutoffTime,
    timestamp
  } = filters.getFiltersForApi();
  const {
    error: errorGettingRevision,
    loading: loadingRevision,
    fetchMore,
    data: revisionData,
    networkStatus
  } = useQuery(GET_FLOW_REVISIONS, {
    variables: {
      flowId: flowId,
      userId,
      cutoffTime,
      timestamp
    },
    skip: !flowId,
    notifyOnNetworkStatusChange: true
  });
  const {
    data,
    error: errorGettingFlow,
    loading: loadingFlow
  } = useQuery(GET_FLOW_BY_ID, {
    variables: {
      // @ts-expect-error FIXME: update code for `TypedDocumentNode` changes
      flowId
    },
    skip: !flowId
  });
  const isRevisionRollupMigrationUngated = isUngated(Gates.REVISION_ROLLUP_MIGRATION);
  const {
    data: revisionRollupData,
    loading: revisionRollupLoading
  } = useAuditEventRollups(revisionData, flowId, filters, isRevisionRollupMigrationUngated);
  useEffect(() => {
    const results = get(revisionRollupData, ['results']);
    const after = get(revisionRollupData, ['paging', 'next', 'after'], undefined);
    setAuditEvents(results);
    setNextPage(after);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisionRollupData]);
  const handleFilterChange = (filterKey, filterValue) => {
    const updatedFilters = filterKey === AuditEventHistoryFilterKeys.RANGE_TYPE ? filters.updateFilterFromDateRange(filterValue) : filters.updateFilter(filterKey, filterValue);
    setFilters(updatedFilters);
    const searchFiltersObject = new URLSearchParams(updatedFilters.getSearchFilters());

    // This is to handle clear buttons with in each date picker field
    let removeProps;
    if (filterKey === AuditEventHistoryFilterKeys.RANGE_TYPE && filterValue.presetId === ALL_PRESET_ID) {
      removeProps = [AuditEventHistoryFilterKeys.START_DATE, AuditEventHistoryFilterKeys.END_DATE, AuditEventHistoryFilterKeys.RANGE_TYPE];
    }
    atomicQueryUpdate(Object.fromEntries(searchFiltersObject.entries()), removeProps, history, false);
  };
  const handleClearFilters = () => {
    const updatedFilters = filters.clearFilters();
    setFilters(updatedFilters);
    atomicQueryUpdate({}, Object.values(AuditEventHistoryFilterKeys), history, false);
    trackEvent(Trackables.HISTORY_FILTERS_CLEAR_FILTERS);
  };
  if (errorGettingRevision || errorGettingFlow) {
    return /*#__PURE__*/_jsxs(UIPanel, {
      width: SIDE_PANEL_WIDTH,
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(UIPanelHeader, {
        children: /*#__PURE__*/_jsx("h3", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.revisionRollupPanel.header"
          })
        })
      }), /*#__PURE__*/_jsx(UIPanelBody, {
        children: /*#__PURE__*/_jsx(UIPanelSection, {
          children: /*#__PURE__*/_jsx(GenericErrorMessage, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-management.revisionRollupPanel.loadingError"
            }),
            children: renderFailureNavMarker && renderFailureNavMarker()
          })
        })
      })]
    });
  }
  const isLoading = loadingRevision || loadingFlow || revisionRollupLoading;
  const flow = data && data[FLOW_BY_ID_FIELDNAME];
  const AUDIT_EVENT_FILTER_OPTIONS = Object.keys(AuditEventTypesLookup).map(auditEventTypeKey => ({
    text: I18n.text(`automation-ui-management.auditEvent.auditEventTypes.${AuditEventTypesLookup[auditEventTypeKey]}`),
    value: AuditEventTypesLookup[auditEventTypeKey]
  }));
  return /*#__PURE__*/_jsxs(UIPanel, {
    width: SIDE_PANEL_WIDTH,
    children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(UIPanelHeader, {
      children: /*#__PURE__*/_jsx("h3", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.revisionRollupPanel.header"
        })
      })
    }), /*#__PURE__*/_jsxs(UIPanelBody, {
      children: [flow && /*#__PURE__*/_jsx(AuditHistoryFilters, {
        filters: filters,
        eventTypeFilterOptions: AUDIT_EVENT_FILTER_OPTIONS,
        defaultCustomDateRange: getDefaultCustomDateRangeForFlow(flow, null, flow),
        onFilterChange: handleFilterChange,
        onClearFilters: handleClearFilters
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(UIAbstractProgress, {
          autoStart: isLoading,
          incrementFactor: isLoading ? 0.15 : 0.5,
          render: ({
            value
          }) => /*#__PURE__*/_jsxs(_Fragment, {
            children: [isLoading && /*#__PURE__*/_jsx("span", {
              "data-loading": true
            }), /*#__PURE__*/_jsx(UINanoProgress, {
              value: value,
              animateOnComplete: !isLoading
            })]
          })
        }), /*#__PURE__*/_jsxs(UITable, {
          condensed: true,
          children: [/*#__PURE__*/_jsxs("colgroup", {
            children: [/*#__PURE__*/_jsx("col", {}), /*#__PURE__*/_jsx("col", {
              style: {
                width: ALIGNED_TIMESTAMP_COLUMN_WIDTH
              }
            })]
          }), /*#__PURE__*/_jsx("thead", {
            children: /*#__PURE__*/_jsxs("tr", {
              children: [/*#__PURE__*/_jsx("th", {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "automation-ui-management.revisionRollupPanel.tableHeaders.userAndChange"
                })
              }), /*#__PURE__*/_jsx("th", {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "automation-ui-management.revisionRollupPanel.tableHeaders.timestamp"
                })
              })]
            })
          }), /*#__PURE__*/_jsx("tbody", {
            children: auditEvents.getFilteredAuditEvents(filters).map(auditEvent => {
              const isExpanded = expandedRowKey === auditEvent.getTimestamp();
              return /*#__PURE__*/_jsx(RevisionRollupRow, {
                auditEvent: auditEvent,
                flowId: flowId,
                open: expandedRowKey === auditEvent.getTimestamp(),
                onOpenChange: ({
                  target: {
                    value
                  }
                }) => {
                  setExandedRowKey(value);
                },
                onRevisionSelect: onRevisionSelect
              }, `${auditEvent.getTimestamp()}-${isExpanded}`);
            })
          })]
        }), !isLoading && renderNavMarker && renderNavMarker()]
      }), nextPage && /*#__PURE__*/_jsx(UIButtonWrapper, {
        justify: "center",
        children: /*#__PURE__*/_jsx(UILoadingButton, {
          use: "tertiary-light",
          size: "extra-small"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          ,
          onClick: () => fetchMore({
            variables: {
              after: nextPage
            }
          }),
          loading: networkStatus === NetworkStatus.fetchMore,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.revisionRollupPanel.loadMore"
          })
        })
      })]
    })]
  });
};
export default compose(wireAuthConsumer)(RevisionRollupPanel);